import { applyPhoneMask, getPhoneState, normalizeDashes } from '@/js/autorization/phone.utils';
import { createCooldownWithLock } from '@/js/autorization/timer.utils';
import { initPasswordToggles } from '@/js/autorization/passwordTogler';

(() => {
  const global = window;

  /* =========================
   * Base helpers
   * ========================= */

  const trim = (v) => (v ?? '').toString().trim();

  const setDisabled = (el, state) => {
    if (!el) return;
    el.disabled = !!state;
  };

  const setVisible = (el, isVisible) => {
    if (!el) return;
    el.style.display = isVisible ? '' : 'none';
  };

  const setErrorOnGroup = (errorEl, isError) => {
    const inputEl = errorEl?.previousElementSibling?.querySelector?.('input') || errorEl?.previousElementSibling;
    if (!inputEl || inputEl.tagName !== 'INPUT') return;
    inputEl.classList.toggle('input--error', !!isError);
    inputEl.setAttribute('aria-invalid', !!isError);
    if (isError) inputEl.focus();
  };

  const postFormUrlEncoded = async (url, dataObj) => {
    const body = new URLSearchParams();
    Object.entries(dataObj || {}).forEach(([k, v]) => body.append(k, v ?? ''));

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      },
      body: body.toString(),
      credentials: 'same-origin',
    });

    return await res.json();
  };

  const fetchJson = async (url, payloadObj) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(payloadObj),
      credentials: 'same-origin',
    });

    const text = await res.text();
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      return {};
    }
  };

  const syncLoginRedirectFields = (formEl) => {
    if (!formEl) return;

    const params = new URLSearchParams(window.location.search);
    const returnTo = trim(params.get('return_to'));

    if (!returnTo) return;

    const setHidden = (name, value) => {
      let input = formEl.querySelector(`input[name="${name}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        formEl.appendChild(input);
      }

      input.value = value;
    };

    setHidden('return_to', returnTo);
    setHidden('checkout_url', returnTo);
  };

  // ✅ robust v2 token read (works even if captcha is auto-rendered and widgetId is unknown)
  const getRecaptchaV2TokenFromNode = (node) => {
    try {
      const ta = node?.querySelector?.('textarea[name="g-recaptcha-response"]');
      return (ta?.value || '').trim();
    } catch (e) {
      return '';
    }
  };

  const getRecaptchaForResend = async ({ v3Key, action }) => {
    // v3 first
    if (typeof grecaptcha !== 'undefined' && grecaptcha?.execute && v3Key) {
      try {
        const token = await grecaptcha.execute(v3Key, { action });
        return { token, ver: 'v3', action };
      } catch (e) {}
    }

    // fallback v2 (best effort)
    if (typeof grecaptcha !== 'undefined' && grecaptcha?.getResponse) {
      try {
        const token = grecaptcha.getResponse(0) || '';
        return { token, ver: 'v2', action };
      } catch (e) {}
    }

    return { token: '', ver: '', action };
  };

  /* =========================
   * Shared: section toggles
   * ========================= */

  const UI = {
    toggleBlocks({ hideEl, showEl }) {
      hideEl?.classList.add('hide');
      showEl?.classList.remove('hide');
    },
    setText(selOrEl, text) {
      const el = typeof selOrEl === 'string' ? document.querySelector(selOrEl) : selOrEl;
      if (el) el.textContent = text ?? '';
    },
  };

  /* =========================
   * Recover by phone (moved here)
   * ========================= */

  const RecoverForm = (() => {
    const state = {
      form: null,
      email: null,
      phone: null,
      emailErr: null,
      phoneErr: null,
      recaptchaErr: null,
      submitBtn: null,
      phoneMask: null,
      recaptchaWidgetId: null,
    };

    const init = () => {
      state.form = document.querySelector('#recoverForm');
      if (!state.form) return;
      initPasswordToggles();
      state.email = document.querySelector('#RecoverEmail');
      state.phone = document.querySelector('#recoverPhone');

      state.emailErr = document.querySelector('#recoverEmailErr');
      state.phoneErr = document.querySelector('#recoverPhoneErr');
      state.recaptchaErr = document.querySelector('#recaptchaRecoverError');

      state.submitBtn = state.form.querySelector('[type="submit"]');

      setVisible(state.emailErr, false);
      setVisible(state.phoneErr, false);
      setVisible(state.recaptchaErr, false);

      state.phoneMask = state.phone ? applyPhoneMask(state.phone) : null;

      initRecaptchaV2();

      state.form.addEventListener('submit', onSubmit);
    };

    const initRecaptchaV2 = () => {
      state.recaptchaWidgetId = null;

      if (typeof grecaptcha === 'undefined' || !grecaptcha?.ready) return;

      grecaptcha.ready(() => {
        const node = document.querySelector('[data-id="recover-recaptcha"]');
        if (!node) return;

        try {
          const alreadyRendered = !!node.querySelector('iframe');
          if (!alreadyRendered) {
            state.recaptchaWidgetId = grecaptcha.render(node, {
              sitekey: global?.LoginPhone?.recaptcha_key_v2,
            });
          }
        } catch (e) {}
      });
    };

    const getRecaptchaV2Token = () => {
      if (typeof grecaptcha === 'undefined' || !grecaptcha?.getResponse) return '';
      if (typeof state.recaptchaWidgetId === 'number') return grecaptcha.getResponse(state.recaptchaWidgetId) || '';
      return grecaptcha.getResponse() || '';
    };

    const resetErrors = () => {
      if (state.phoneErr) {
        setVisible(state.phoneErr, false);
        setErrorOnGroup(state.phoneErr, false);
      }
      if (state.emailErr) {
        setVisible(state.emailErr, false);
        setErrorOnGroup(state.emailErr, false);
      }
      if (state.recaptchaErr) setVisible(state.recaptchaErr, false);
    };

    const onSubmit = async (e) => {
      const emailVal = (state.email?.value || '').trim();
      const emailHasAny = emailVal.length > 0;

      const phoneMasked = normalizeDashes((state.phone?.value || '').toString());
      const st = getPhoneState(phoneMasked, state.phoneMask);

      resetErrors();

      if (!emailHasAny && !st.hasAnyInput) {
        const requiredMsg = global?.LoginPhone?.msg?.required || 'Required';

        if (state.phoneErr) {
          state.phoneErr.innerHTML = requiredMsg;
          setVisible(state.phoneErr, true);
          setErrorOnGroup(state.phoneErr, true);
        }
        if (state.emailErr) {
          state.emailErr.innerHTML = requiredMsg;
          setVisible(state.emailErr, true);
          setErrorOnGroup(state.emailErr, true);
        }

        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['phone', 'email']);
        e.preventDefault();
        return false;
      }

      if (emailHasAny && !st.hasAnyInput) {
        // e.preventDefault();

        // const redirectUrl = global?.LoginConf?.urlEmailRecoverRedirect;

        // window.location.href = redirectUrl;
        // return false;
        return true;
      }

      if (st.hasAnyInput && !st.isComplete) {
        if (state.phoneErr) {
          state.phoneErr.innerHTML = global?.LoginConf?.msg?.incorrectPhone || 'Incorrect phone';
          setVisible(state.phoneErr, true);
          setErrorOnGroup(state.phoneErr, true);
        }
        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['phone']);
        e.preventDefault();
        return false;
      }

      if (!st.formatOk || !st.operatorOk) {
        if (state.phoneErr) {
          state.phoneErr.innerHTML = global?.LoginConf?.msg?.incorrectPhone || 'Incorrect phone';
          setVisible(state.phoneErr, true);
          setErrorOnGroup(state.phoneErr, true);
        }
        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['phone']);
        e.preventDefault();
        return false;
      }

      e.preventDefault();

      const recToken = getRecaptchaV2Token();
      if (!recToken) {
        if (state.recaptchaErr) {
          state.recaptchaErr.textContent = global?.LoginPhone?.msg?.recaptchaNotFound || 'reCAPTCHA not found';
          setVisible(state.recaptchaErr, true);
        }
        if (state.phoneErr) setErrorOnGroup(state.phoneErr, true);
        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['phone']);
        return false;
      }

      setDisabled(state.submitBtn, true);

      try {
        const data = await fetchJson(global?.LoginConf?.urlRecover, {
          phone: st.masked,
          rec_token: recToken,
        });

        if (data?.response && data?.data?.id) {
          try {
            sessionStorage.setItem(
              'recover_phone_data',
              JSON.stringify({ phone: st.masked, digits: st.digits10, time: Date.now() })
            );
          } catch (e) {}

          const url = `${global?.LoginConf?.urlConfirmRecover}?id=${encodeURIComponent(
            data?.data?.id
          )}&pass=${encodeURIComponent(data?.data?.input_password || '')}`;

          window.location.href = url;
          return false;
        }

        const msg = data?.message ? data.message : global?.LoginConf?.msg?.tryAgain || 'Try again';
        if (state.phoneErr) {
          state.phoneErr.innerHTML = msg;
          setVisible(state.phoneErr, true);
          setErrorOnGroup(state.phoneErr, true);
        }

        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['server', data?.code]);
        return false;
      } catch (err) {
        const msg = global?.LoginConf?.msg?.tryAgain || 'Try again';
        if (state.phoneErr) {
          state.phoneErr.innerHTML = msg;
          setVisible(state.phoneErr, true);
          setErrorOnGroup(state.phoneErr, true);
        }
        global?.GTM?.unsuccessfulPasswordRecoveryEvent?.(['server']);
        return false;
      } finally {
        setDisabled(state.submitBtn, false);
      }
    };

    document.addEventListener('DOMContentLoaded', init);

    return { init };
  })();

  /* =========================
   * Login: Confirm SMS (timer via shared util)
   * ========================= */

  const LoginSmsForm = (() => {
    const COOLDOWN_SEC = 60;
    const RESEND_LOCK_KEY = 'login_sms_resend_locked';

    const state = {
      loginBlock: null,
      confirmBlock: null,
      form: null,

      smsInput: null,
      smsError: null,
      resendBtn: null,
      confirmBtn: null,
      backBtn: null,

      timerEl: null,
      cooldown: null,

      recaptchaWidgetId: null,
    };

    const isResendLocked = () => sessionStorage.getItem(RESEND_LOCK_KEY) === '1';
    const lockResend = () => sessionStorage.setItem(RESEND_LOCK_KEY, '1');
    const unlockResend = () => sessionStorage.removeItem(RESEND_LOCK_KEY);

    const init = () => {
      state.loginBlock = document.querySelector('[data-login-form]');
      state.confirmBlock = document.querySelector('[data-confirm-sms]');
      state.form = document.querySelector('#confirmSmsForm');

      state.smsInput = document.querySelector('#loginSms');
      state.smsError = document.querySelector('#smsError');
      state.resendBtn = document.querySelector('#resendSmsBtn');
      state.confirmBtn = document.querySelector('#loginSmsBtn');
      state.backBtn = document.querySelector('#confirmSmsBtnBack');
      state.timerEl = document.querySelector('#confirmSmsTimer');

      state.cooldown = createCooldownWithLock({
        seconds: COOLDOWN_SEC,
        lockedFn: isResendLocked,
        onTick: (secLeft) => {
          if (!state.timerEl) return;
          state.timerEl.textContent = `${Math.max(0, secLeft | 0)} сек`;
        },
        onEnable: () => {
          setDisabled(state.resendBtn, false);
        },
      });

      state.smsInput?.addEventListener('keyup', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) onConfirmSms();
      });

      state.confirmBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onConfirmSms();
      });

      state.resendBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onResendClick();
      });

      state.backBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onBack();
      });

      initRecaptchaV2();
    };

    const initRecaptchaV2 = () => {
      if (typeof grecaptcha === 'undefined' || !grecaptcha?.ready) return;

      grecaptcha.ready(() => {
        const node = document.querySelector('[data-id="login-recaptcha"]');
        if (!node) return;

        try {
          const alreadyRendered = !!node.querySelector('iframe');
          if (!alreadyRendered) {
            state.recaptchaWidgetId = grecaptcha.render(node, {
              sitekey: global?.LoginPhone?.recaptcha_key_v2,
            });
          }
        } catch (e) {}
      });
    };

    const resetRecaptcha = () => {
      if (typeof grecaptcha === 'undefined' || !grecaptcha?.reset) return;
      if (typeof state.recaptchaWidgetId === 'number') grecaptcha.reset(state.recaptchaWidgetId);
      else grecaptcha.reset();
    };

    const startCooldown = (seconds = COOLDOWN_SEC) => {
      setDisabled(state.resendBtn, true);
      state.cooldown?.start(seconds);
    };

    const stopCooldown = () => state.cooldown?.stop?.();

    const renderPhoneLabel = () => {
      const raw = sessionStorage.getItem('login_phone_data');
      if (!raw) return;
      try {
        const phone = JSON.parse(raw)?.phone;
        UI.setText('#confirmSmsPhone', phone || '');
      } catch (e) {}
    };

    const show = () => {
      UI.toggleBlocks({ hideEl: state.loginBlock, showEl: state.confirmBlock });
      setVisible(state.form, true);

      renderPhoneLabel();

      unlockResend();
      startCooldown(COOLDOWN_SEC);
    };

    const hide = () => {
      UI.toggleBlocks({ hideEl: state.confirmBlock, showEl: state.loginBlock });

      if (state.smsError) setVisible(state.smsError, false);

      stopCooldown();

      setDisabled(state.resendBtn, isResendLocked());

      resetRecaptcha();
    };

    const onBack = () => {
      hide();
      global.SendSmsForm?.show?.();
    };

    const onConfirmSms = () => {
      if (state.smsError) {
        setVisible(state.smsError, false);
        setErrorOnGroup(state.smsError, false);
      }

      if (!trim(state.smsInput?.value)) {
        if (state.smsError) {
          state.smsError.textContent = global?.LoginPhone?.msg?.required || 'Required';
          setVisible(state.smsError, true);
          setErrorOnGroup(state.smsError, true);
        }
        return;
      }

      confirmSms();
    };

    const confirmSms = async () => {
      let gaId = '';

      setDisabled(state.smsInput, true);
      setDisabled(state.confirmBtn, true);

      if (state.smsError) setVisible(state.smsError, false);

      if (global.GaClientId?.getId) gaId = global.GaClientId.getId();

      const checkoutUrl = document.querySelector('[name="checkout_url"]')?.value || '';

      const reqData = {
        customer_id: global.SendSmsForm?.getCustomerId?.(),
        code: trim(state.smsInput?.value),
        cart_items: global?.LoginPhone?.cartItemsCount,
        uri: checkoutUrl,
        ga_id: gaId,
      };

      try {
        const data = await postFormUrlEncoded(global?.LoginPhone?.urlSmsConfirm, reqData);

        if (data?.response) {
          try {
            window.EsTracking?.onLogin?.();
          } catch (e) {}

          global.SendSmsForm?.markCodeUsed?.();

          const loginUrl = data?.data?.loginUrl;
          console.log(data);
          console.log(loginUrl);
          if (loginUrl) window.location.href = loginUrl;
          return;
        }

        const msg = data?.validationMessages?.[0]?.message || global?.LoginPhone?.msg?.tryAgain || 'Try again';
        if (state.smsError) {
          state.smsError.textContent = msg;
          setVisible(state.smsError, true);
          setErrorOnGroup(state.smsError, true);
        }
      } catch (e) {
        if (state.smsError) {
          state.smsError.textContent = global?.LoginPhone?.msg?.tryAgain || 'Try again';
          setVisible(state.smsError, true);
        }
      } finally {
        setDisabled(state.smsInput, false);
        setDisabled(state.confirmBtn, false);
      }
    };

    const onResendClick = async () => {
      if (isResendLocked()) return;
      if (state.cooldown?.getSecondsLeft?.() > 0) return;

      setDisabled(state.resendBtn, true);

      try {
        let phone = '';
        try {
          const raw = sessionStorage.getItem('login_phone_data');
          phone = raw ? JSON.parse(raw)?.phone || '' : '';
        } catch (e) {}

        const { token, ver, action } = await getRecaptchaForResend({
          v3Key: global?.LoginPhone?.recaptcha_key_v3,
          action: 'customer_login',
        });

        if (!token) {
          if (state.smsError) {
            state.smsError.textContent = global?.LoginPhone?.msg?.recaptchaNotFound || 'reCAPTCHA not found';
            setVisible(state.smsError, true);
          }
          setDisabled(state.resendBtn, isResendLocked());
          return;
        }

        const resendUrl = global?.LoginPhone?.urlSmsResend || global?.LoginPhone?.urlSms;

        const data = await postFormUrlEncoded(resendUrl, {
          phone,
          rec_token: token,
          rec_ver: ver,
          rec_action: action,
        });

        if (data?.response) {
          lockResend();
          startCooldown(COOLDOWN_SEC);
          return;
        }

        const msg = data?.validationMessages?.[0]?.message || global?.LoginPhone?.msg?.tryAgain || 'Try again';
        if (state.smsError) {
          state.smsError.textContent = msg;
          setVisible(state.smsError, true);
          setErrorOnGroup(state.smsError, true);
        }
        setDisabled(state.resendBtn, false);
      } catch (e) {
        if (state.smsError) {
          state.smsError.textContent = global?.LoginPhone?.msg?.tryAgain || 'Try again';
          setVisible(state.smsError, true);
        }
        setDisabled(state.resendBtn, false);
      }
    };

    document.addEventListener('DOMContentLoaded', init);

    return { show, hide };
  })();

  global.LoginSmsForm = LoginSmsForm;

  /* =========================
   * Login: Send SMS
   * ========================= */

  const SendSmsForm = (() => {
    const STORE_KEY = 'login_phone_data';

    const state = {
      form: null,
      loginBlock: null,

      loginWithPassBtnContainer: null,

      phoneInput: null,
      phoneMask: null,
      sendSmsBtn: null,
      phoneError: null,
      recaptchaError: null,
      recaptchaNode: null,

      loginWithPassBtn: null,
      loginPassForm: null,
      loginPassNativeForm: null,
      toSmsSendFormBtn: null,

      recoverPasswordBtn: null,
      backToLoginBtn: null,
      hideRecoverPasswordLink: null,
      loginFormBlock: null,
      recoverFormBlock: null,
    };

    const init = () => {
      state.form = document.querySelector('#sendSmsForm');
      state.loginBlock = document.querySelector('[data-login-form]');

      state.loginWithPassBtnContainer = document.querySelector('#loginWithPassBtn');

      state.phoneInput = document.querySelector('#loginPhone');
      state.sendSmsBtn = document.querySelector('#sendSmsBtn');
      state.phoneError = document.querySelector('#phoneError');
      state.recaptchaError = document.querySelector('#recaptchaError');

      state.recaptchaNode = document.querySelector('[data-id="login-recaptcha"]');

      state.loginWithPassBtn = document.querySelector('#loginWithPassBtn');
      state.loginPassForm = document.querySelector('#loginPassForm');
      state.loginPassNativeForm = state.loginPassForm?.querySelector('form') || null;
      state.toSmsSendFormBtn = document.querySelector('#toSmsSendFormBtn');

      state.recoverPasswordBtn = document.querySelector('#RecoverPassword');
      state.backToLoginBtn = document.querySelector('[data-back-to-login]');

      state.hideRecoverPasswordLink = document.querySelector('#HideRecoverPasswordLink');
      state.loginFormBlock = document.querySelector('[data-login-form]');
      state.recoverFormBlock = document.querySelector('[data-recover-form]');

      state.phoneMask = state.phoneInput
        ? applyPhoneMask(state.phoneInput)
        : null;

      state.phoneInput?.addEventListener('keyup', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) onSendSms();
      });

      state.sendSmsBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onSendSms();
      });

      state.loginWithPassBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onLoginWithPass();
      });

      state.toSmsSendFormBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onToSmsSendForm();
      });

      state.recoverPasswordBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onRecoverPassword();
      });

      state.backToLoginBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        onBackToLogin();
      });

      state.hideRecoverPasswordLink?.addEventListener('click', (e) => {
        e.preventDefault();
        onHideRecoverPassword();
      });

      state.loginPassNativeForm?.addEventListener('submit', () => {
        syncLoginRedirectFields(state.loginPassNativeForm);
      });

      if (global?.LoginPhone?.hasPassFormError) onLoginWithPass();

      restoreData();
      syncLoginRedirectFields(state.loginPassNativeForm);
      setFormsVisibility();
    };

    const scrollTopSmooth = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const onRecoverPassword = () => {
      setVisible(state.form, false);
      setVisible(state.loginPassForm, false);
      setVisible(state.loginWithPassBtnContainer, false);

      state.loginFormBlock?.classList.add('hide');
      state.recoverFormBlock?.classList.remove('hide');

      scrollTopSmooth();
    };

    const onBackToLogin = () => {
      state.recoverFormBlock?.classList.add('hide');
      state.loginFormBlock?.classList.remove('hide');

      setVisible(state.form, true);
      setVisible(state.loginWithPassBtnContainer, true);

      scrollTopSmooth();
    };

    const onHideRecoverPassword = () => {
      state.recoverFormBlock?.classList.add('hide');
      state.loginFormBlock?.classList.remove('hide');
      setVisible(state.loginPassForm, true);
      scrollTopSmooth();
    };

    const onToSmsSendForm = () => {
      setVisible(state.loginPassForm, false);
      setVisible(state.form, true);

      setVisible(state.loginWithPassBtnContainer, true);
      scrollTopSmooth();
    };

    const onLoginWithPass = () => {
      sessionStorage.setItem('methodLogin', 'email');
      localStorage.setItem('custom_event', 'event_auth');

      syncLoginRedirectFields(state.loginPassNativeForm);

      setVisible(state.loginPassForm, true);
      setVisible(state.form, false);

      setVisible(state.loginWithPassBtnContainer, false);
      scrollTopSmooth();
    };

    const onSendSms = () => {
      sessionStorage.setItem('methodLogin', 'phone');
      localStorage.setItem('custom_event', 'event_auth');

      if (state.phoneError) {
        setVisible(state.phoneError, false);
        setErrorOnGroup(state.phoneError, false);
      }
      if (state.recaptchaError) setVisible(state.recaptchaError, false);

      const st = getPhoneState(state.phoneInput?.value || '', state.phoneMask);

      if (!st.isComplete) {
        global?.GTM?.unsuccessfulLoginEvent?.('phone');
        if (state.phoneError) {
          state.phoneError.textContent = global?.LoginPhone?.msg?.required || 'Required';
          setVisible(state.phoneError, true);
          setErrorOnGroup(state.phoneError, true);
        }
        return;
      }

      if (!st.formatOk || !st.operatorOk) {
        global?.GTM?.unsuccessfulLoginEvent?.('phone');
        if (state.phoneError) {
          state.phoneError.textContent = global?.LoginPhone?.msg?.incorrectPhone || 'Incorrect phone';
          setVisible(state.phoneError, true);
          setErrorOnGroup(state.phoneError, true);
        }
        return;
      }

      const recToken = getRecaptchaV2TokenFromNode(state.recaptchaNode);
      if (!recToken) {
        if (state.recaptchaError) {
          state.recaptchaError.textContent = global?.LoginPhone?.msg?.recaptchaNotFound || 'reCAPTCHA not found';
          setVisible(state.recaptchaError, true);
        }
        return;
      }

      sendSms(recToken);
    };

    const sendSms = async (recToken) => {
      setDisabled(state.phoneInput, true);
      setDisabled(state.sendSmsBtn, true);

      if (state.phoneError) {
        setVisible(state.phoneError, false);
        setErrorOnGroup(state.phoneError, false);
      }

      try {
        const data = await postFormUrlEncoded(global?.LoginPhone?.urlSms, {
          phone: state.phoneInput?.value || '',
          rec_token: recToken || '',
        });

        if (data?.response && data?.data?.customer_id) {
          savePhoneToStore(data?.data?.customer_id);
          global.LoginSmsForm?.show?.();
          return;
        }

        if (typeof data?.data === 'object' && typeof data?.data?.no_shopify_customer !== 'undefined') {
          sessionStorage.setItem('reg_phone', state.phoneInput?.value || '');
          window.location.href = global?.LoginPhone?.urlRegister;
          return;
        }

        global?.GTM?.unsuccessfulLoginEvent?.('phone');
        const msg = data?.validationMessages?.[0]?.message || global?.LoginPhone?.msg?.tryAgain || 'Try again';

        if (state.phoneError) {
          state.phoneError.textContent = msg;
          setVisible(state.phoneError, true);
          setErrorOnGroup(state.phoneError, true);
        }
      } catch (e) {
        global?.GTM?.unsuccessfulLoginEvent?.('phone');
        if (state.phoneError) {
          state.phoneError.textContent = global?.LoginPhone?.msg?.tryAgain || 'Try again';
          setVisible(state.phoneError, true);
          setErrorOnGroup(state.phoneError, true);
        }
      } finally {
        setDisabled(state.phoneInput, false);
        setDisabled(state.sendSmsBtn, false);
      }
    };

    const setStoreData = (data) => {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
      } catch (e) {}
    };

    const getStoreData = () => {
      try {
        const raw = sessionStorage.getItem(STORE_KEY);
        return raw ? JSON.parse(raw) : false;
      } catch (e) {
        return false;
      }
    };

    const savePhoneToStore = (dbCustomerId) => {
      setStoreData({
        phone: state.phoneInput?.value || '',
        customer_id: dbCustomerId,
        time: Date.now(),
      });
    };

    const restoreData = () => {
      const data = getStoreData();
      if (data?.phone && state.phoneInput) state.phoneInput.value = data.phone;
    };

    const setFormsVisibility = () => {
      const data = getStoreData();
      if (!data || !data.customer_id) return;
      if (Date.now() - data.time < 5 * 60 * 1000) global.LoginSmsForm?.show?.();
    };

    const markCodeUsed = () => {
      const data = getStoreData();
      if (!data) return;
      data.time = 0;
      setStoreData(data);
    };

    const getCustomerId = () => {
      const data = getStoreData();
      return data ? data.customer_id : false;
    };

    const show = () => setVisible(state.form, true);
    const hide = () => setVisible(state.form, false);

    document.addEventListener('DOMContentLoaded', init);

    return { show, hide, getCustomerId, markCodeUsed };
  })();

  global.SendSmsForm = SendSmsForm;
  global.RecoverForm = RecoverForm;
})();
